// src/components/ActivityTracker.jsx
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { logout } from '../auth';
import config from '../config';
const reactServer = config.reactServer;

const ActivityTracker: React.FC = () => {
  const navigate = useNavigate();
  // Use refs to maintain timer references across re-renders
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const tokenRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const handleInactivity = () => {
    // console.log('Inactivity timeout reached, logging out...');
    logout();
    navigate('/login', {
      state: { message: 'U bent uitgelogd wegens inactiviteit' }
    });
  };

  const resetTimer = () => {
    // Clear existing timers
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current);
    }
  
    // Set inactivity timer
    inactivityTimerRef.current = setTimeout(() => {
      handleInactivity();
    }, 8 * 60 * 60 * 1000); // 8 hours
    
    // Set token refresh interval - refresh every 7 hours
    // This gives a 1-hour buffer before the 8-hour token expires
    tokenRefreshIntervalRef.current = setInterval(() => {
      refreshToken();
    }, 7 * 60 * 60 * 1000); // 7 hours
    
    // Also immediately check if we need to refresh token
    checkAndRefreshToken();
  };
  
  // Immediate check if token needs refresh
  const checkAndRefreshToken = () => {
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      // console.log('No token found in localStorage');
      return;
    }
    
    try {
      // Parse JWT to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeRemaining = Math.floor((expirationTime - currentTime) / 1000 / 60); // minutes
      
      // console.log(`Token expires in ${timeRemaining} minutes`);
      
      // If token expires in less than 1 hour, refresh it immediately
      if (expirationTime - currentTime < 60 * 60 * 1000) {
        // console.log('Token will expire soon, refreshing now...');
        refreshToken();
      }
    } catch (e) {
      console.error('Error checking token expiration:', e);
    }
  };
  
  const refreshToken = async () => {
    try {
      // console.log('Attempting to refresh token...');
      const currentToken = localStorage.getItem('jwtToken'); // Changed from 'token' to 'jwtToken'
      
      if (!currentToken) {
        throw new Error('No token found in localStorage');
      }
      
      // Use the current token for refresh - no separate refresh token needed
      const response = await fetch(`${reactServer}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Error refreshing token: ${response.status}`);
      }
      
      const data = await response.json();
      if (data && data.access_token) {
        // Update token in local storage
        localStorage.setItem('jwtToken', data.access_token); // Changed from 'token' to 'jwtToken'
        // console.log('Token refreshed successfully');
      } else {
        throw new Error('Invalid response format from refresh endpoint');
      }
    } catch (error) {
      console.error('Failed to refresh token:', error);
      // If refresh fails, logout user
      handleInactivity();
    }
  };

  useEffect(() => {
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

    const handleActivity = () => {
      resetTimer();
    };

    // Add all activity event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Initialize timers on component mount
    resetTimer();

    // Cleanup on component unmount
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
      }
    };
  }, []);

  return null;
};

export default ActivityTracker;